/* @flow */
/**
 * Redux Toolkit slice for search panel dropdown data and user display preferences.
 * Loads lookup lists, selected filters, column ordering, row preferences, and fields of play.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner, updateDialogStates } from '../dialogStates/dialogStatesSlice';
import fieldMapping from '../../constants/fieldMappings/poSearchFieldMapping';
import { initialSearchFieldsState } from '../../constants/staticData';
import { poItemStatusSelect } from '../mock/searchPanelMockData';
import { checkUserPermission } from '../../helpers/userPermission';
// import { showForGoodSupplier } from '../../helpers/envServer';

const name = 'searchpaneldata';

function createInitialState() {
  return {
    [fieldMapping.PO_LINE_ITEM_STATUS]: [],
    [fieldMapping.VENDOR_CODE]: [],
    [fieldMapping.PMO_DEC_CODE]: [],
    [fieldMapping.POSEARCH_GOODS_SUPPLIER_CODE]: [],
    [fieldMapping.MANUFACTURING_CODE]: [],
    'poHeader.searchcriteria': { ...initialSearchFieldsState },
    selectedFilterOptions: [],
    selectedColumnOrderOptions: [],
    rowPerPageOption: ['100'],
    fieldsOfPlayList: []
  };
}

/**
 * Reducer that stores PO line item status dropdown options in search panel state.
 */
function setLineStatusDropdownValues(state: Object, action: Object) {
  return {
    ...state,
    [fieldMapping.PO_LINE_ITEM_STATUS]: action.payload
  };
}

/**
 * Reducer that stores factory code dropdown options in search panel state.
 */
function setFactoryCodeDropdown(state: Object, action: Object) {
  return {
    ...state,
    [fieldMapping.VENDOR_CODE]: action.payload
  };
}

/**
 * Reducer that stores goods supplier dropdown options in search panel state.
 */
function setGoodsSupplierDropdown(state: Object, action: Object) {
  return {
    ...state,
    [fieldMapping.POSEARCH_GOODS_SUPPLIER_CODE]: action.payload
  };
}

/**
 * Reducer that stores manufacturing code dropdown options in search panel state.
 */
function setManufacturingCodeDropdown(state: Object, action: Object) {
  return {
    ...state,
    [fieldMapping.MANUFACTURING_CODE]: action.payload
  };
}

/**
 * Reducer that stores PMO/DEC office code dropdown options in search panel state.
 */
function setPMODECCodeDropdownValues(state: Object, action: Object) {
  return {
    ...state,
    [fieldMapping.PMO_DEC_CODE]: action.payload
  };
}

/**
 * Reducer that stores the user-selected default filter list.
 */
function setFilterListValues(state: Object, action: Object) {
  return {
    ...state,
    selectedFilterOptions: action.payload
  };
}

/**
 * Reducer that stores the user-selected column order list.
 */
function setColumnOrderListValues(state: Object, action: Object) {
  return {
    ...state,
    selectedColumnOrderOptions: action.payload
  };
}

/**
 * Reducer that stores the preferred report rows-per-page value.
 */
function setRowPerPagePreference(state: Object, action: Object) {
  return {
    ...state,
    rowPerPageOption: action.payload
  };
}

/**
 * Reducer that stores fields-of-play dropdown options.
 */
function setFieldsOfPlayDropDown(state: Object, action: Object) {
  return {
    ...state,
    fieldsOfPlayList: action.payload
  };
}

/**
 * Builds the reducer map consumed by the search panel slice.
 */
function createReducers() {
  return {
    setLineStatusDropdownValues,
    setFactoryCodeDropdown,
    setGoodsSupplierDropdown,
    setManufacturingCodeDropdown,
    setPMODECCodeDropdownValues,
    setFilterListValues,
    setColumnOrderListValues,
    setRowPerPagePreference,
    setFieldsOfPlayDropDown
  };
}

export const initialState = createInitialState();
export const reducers = createReducers();
const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Determines whether dropdown lookup data is missing and needs to be fetched.
 */
function checkForSearchPanelData(state: Object) {
  const searchPanelData = state.searchpaneldata;
  if (!searchPanelData) {
    return true;
  }
  // Any populated core dropdown means the lookup data is already cached.
  const hasPanelData = searchPanelData[fieldMapping.VENDOR_CODE]?.length > 0
    || searchPanelData[fieldMapping.PMO_DEC_CODE]?.length > 0
    || searchPanelData[fieldMapping.PO_LINE_ITEM_STATUS]?.length > 0;
  return !hasPanelData;
}

/**
 * Normalizes simple lookup values into id/value dropdown option objects.
 */
function mapDropdownOptions(items) {
  return (items ?? []).map((option) => ({ id: option, value: option }));
}

/**
 * Returns line status options allowed for the current user group.
 */
function getModifiedLineStatusDropdown(currentUserGroup) {
  // Users with DPOMLineItemStatus permission can see every status option.
  if (checkUserPermission('DPOMLineItemStatus', currentUserGroup)) {
    return [...poItemStatusSelect];
  }
  return poItemStatusSelect
    .map((option) => {
      if (option.text === 'All') {
        return {
          value: option.value.replace('Unissued,', ''),
          text: option.text
        };
      }
      return option;
    })
    .filter((option) => option.value !== 'Unissued');
}

/**
 * Builds the user profile field list needed for column, filter, and row preferences.
 */
function buildProfileFields(columnType, filterType) {
  // Always request row preferences even when no column or filter profile fields are needed.
  if (!filterType && !columnType) {
    return 'rowsPerPagePreference';
  }
  if (!filterType) {
    return `${columnType},rowsPerPagePreference`;
  }
  if (!columnType) {
    return `${filterType},rowsPerPagePreference`;
  }
  return `${columnType},${filterType},rowsPerPagePreference`;
}

/**
 * Async thunk that loads dropdownLookup data and updates search panel dropdown state.
 */
function getsearchPanelData() {
  return createAsyncThunk(
    `${name}/getsearchPanelData`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      const state = getState();
      // Avoid refetching dropdown lookup data once it is present in Redux state.
      if (checkForSearchPanelData(state)) {
        dispatch(toggleSpinner(true));
        dispatch(updateDialogStates({ lookupApiSpinner: true }));

        request({
          api: 'dropdownLookup',
          method: 'get'
        }, dispatch, getState).then((response: Object) => {
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ lookupApiSpinner: false }));
          const {
            partnerNumber,
            productionNikeLiaisonOffice,
            allManufacturingLocationCodes
          } = response.data ?? {};
          // Attach manufacturing codes to each factory option for dependent dropdown filtering.
          const factoryCodeList = Array.isArray(partnerNumber)
            ? partnerNumber.map((option) => ({
              id: option.partnerNo,
              value: option.partnerNo,
              manufacturingCodes: option.manufacturingCodes ?? []
            }))
            : [];
          dispatch(actions.setFactoryCodeDropdown(factoryCodeList));
          dispatch(actions.setPMODECCodeDropdownValues(
            mapDropdownOptions(productionNikeLiaisonOffice)
          ));
          dispatch(actions.setGoodsSupplierDropdown(
            mapDropdownOptions(allManufacturingLocationCodes)
          ));
          dispatch(actions.setManufacturingCodeDropdown(
            mapDropdownOptions(allManufacturingLocationCodes)
          ));
          if (callback) {
            callback(response.data);
          }
        }).catch((error: Object) => {
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ lookupApiSpinner: false }));
          if (callback) {
            callback(null, error);
          }
        });
      }

      const currentUserGroup = state.auth?.userDetails?.group;
      dispatch(actions.setLineStatusDropdownValues(
        getModifiedLineStatusDropdown(currentUserGroup)
      ));
      return true;
    }
  );
}

/**
 * Async thunk that loads user profile column, filter, and row preferences.
 */
function getFilterAndColumnOrderList() {
  return createAsyncThunk(
    `${name}/getFilterAndColumnOrderList`,
    async (
      { columnType, filterType, callback },
      { getState, dispatch }
    ) => {
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));
      // Request only the profile fields needed by the current preference lookup.
      const fields = buildProfileFields(columnType, filterType);
      request({
        api: 'getUserProfileData',
        method: 'get',
        routeParams: { fields }
      }, dispatch, getState).then((response: Object) => {
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        const profileObject = response.data?.objects?.[0] ?? {};
        const columnOrderOptions = profileObject[columnType] ?? [];
        // Exclude deprecated rate type columns from saved column order preferences.
        const filteredColumnOrderOptions = columnOrderOptions.filter(
          (item) => !item.toLowerCase().includes('ratetypename')
        );
        const filterOptions = profileObject[filterType] ?? [];
        const { rowsPerPagePreference } = profileObject;
        dispatch(actions.setRowPerPagePreference(
          rowsPerPagePreference ? rowsPerPagePreference[0] : '100'
        ));
        dispatch(actions.setFilterListValues(filterOptions));
        dispatch(actions.setColumnOrderListValues(filteredColumnOrderOptions));
      }).catch((error: Object) => {
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Async thunk that calls GET_FIELDS_OF_PLAY and stores fields-of-play dropdown options.
 */
function getFieldsOfPlayData() {
  return createAsyncThunk(
    `${name}/getFieldsOfPlayData`,
    async ({ callback }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'GET_FIELDS_OF_PLAY',
          method: 'get'
        },
        dispatch
      )
        .then((response) => {
          const { fieldsOfPlayInfo } = response.data ?? {};
          // Display fields of play as abbreviation/name labels while keeping the name as the id.
          const fieldsOfPlayList = Array.isArray(fieldsOfPlayInfo)
            ? fieldsOfPlayInfo.map((option) => ({
              id: option.fieldsOfPlayName,
              value: `${option.fieldsOfPlayAbbreviation} | ${option.fieldsOfPlayName}`
            }))
            : [];
          dispatch(actions.setFieldsOfPlayDropDown(fieldsOfPlayList));
          if (callback) {
            callback(response.data);
          }
          dispatch(toggleSpinner(false));
        })
        .catch(() => {
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Creates async thunk action creators exposed by the search panel slice.
 */
function createExtraActions() {
  return {
    getsearchPanelData: getsearchPanelData(),
    getFilterAndColumnOrderList: getFilterAndColumnOrderList(),
    getFieldsOfPlayData: getFieldsOfPlayData()
  };
}

export const extraActions = createExtraActions();

export const searchPanelDataActions = { ...actions, ...extraActions };
export const searchPanelDataReducer = slice.reducer;
